import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import { Notice } from "../../lib/ui";
import "./finance.css";

/*
 * Account statement – port of accountStatement.php (API: GET /api/accounts/statement, backend/ledger.py)
 * Opening balance = completed debits − credits before "from"; running balance per entry; closing balance.
 * Balances show as Dr (debit, ≥ 0) or Cr (credit, < 0). The filters live in the URL, so a statement can be bookmarked.
 * Also serves select_statement (its form fields: account_number, start_date, end_date).
 * "Print statement" prints this page (PHP posted to ../report/statement, which isn't part of the port).
 */

const fmtDate = (d) => new Date(d.replace(" ", "T")).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
const drcr = (v) => (v >= 0 ? "Dr" : "Cr");

export default function AccountStatement() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  // select_statement.php linked here with account_number / start_date / end_date; accept both spellings
  const pick = (a, b) => params.get(a) || params.get(b) || "";
  const [form, setForm] = useState({ account_id: pick("account_id", "account_number"), from_date: pick("from_date", "start_date"), to_date: pick("to_date", "end_date") });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/api/accounts/active").then(setAccounts).catch((e) => setError(e.message)); }, [api]);
  useEffect(() => {
    const q = { account_id: pick("account_id", "account_number"), from_date: pick("from_date", "start_date"), to_date: pick("to_date", "end_date") };
    if (!q.account_id || !q.from_date || !q.to_date) { setData(null); return; }
    setLoading(true); setError(null);
    api.get("/api/accounts/statement", q).then(setData).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [api, params]); // eslint-disable-line react-hooks/exhaustive-deps

  const actions = data?.rows.length ? <button type="button" className="btn" onClick={() => window.print()}><i className="fas fa-print" /> Print statement</button> : null;

  return (
    <main className="pg">
      <PageHeader slug="accountStatement" actions={actions} />
      <Notice notice={error && { tone: "err", text: error }} />
      <div className="panel no-print" style={{ marginBottom: "1.25rem" }}>
        <form className="toolbar" style={{ borderBottom: 0 }} onSubmit={(e) => { e.preventDefault(); setParams(form); }}>
          <div className="field grow"><label htmlFor="as-acc">Account</label>
            <select id="as-acc" required value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
              <option value="">Select account</option>
              {accounts.map((a) => <option key={a.account_number} value={a.account_number}>{a.account_number} – {a.account_name}</option>)}
            </select></div>
          <div className="field"><label htmlFor="as-from">From</label>
            <input id="as-from" type="date" required value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} /></div>
          <div className="field"><label htmlFor="as-to">To</label>
            <input id="as-to" type="date" required value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} /></div>
          <button type="submit" className="btn primary" disabled={loading}>{loading ? "Generating…" : "Generate statement"}</button>
        </form>
      </div>

      {data && (data.rows.length === 0 ? (
        <div className="panel empty"><i className="fas fa-file-circle-xmark" />No transactions for {data.account_id} between {fmtDate(data.from_date)} and {fmtDate(data.to_date)}.</div>
      ) : (
        <section className="panel">
          <div className="as-head">
            <div><h2>{data.account_id} – {data.account_name}</h2><p>{fmtDate(data.from_date)} to {fmtDate(data.to_date)}</p></div>
            <div className="as-bal"><span>Opening balance</span><strong className="mono">{n2(Math.abs(data.opening_balance))} {drcr(data.opening_balance)}</strong></div>
            <div className="as-bal"><span>Closing balance</span><strong className="mono">{n2(Math.abs(data.closing_balance))} {drcr(data.closing_balance)}</strong></div>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>Date</th><th>Reference</th><th className="num">Debit</th><th className="num">Credit</th><th className="num">Running balance</th></tr></thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i}>
                    <td>{fmtDate(r.date)}</td><td className="mono">{r.reference ?? "–"}</td>
                    <td className="num mono">{r.debit > 0 ? n2(r.debit) : "–"}</td>
                    <td className="num mono">{r.credit > 0 ? n2(r.credit) : "–"}</td>
                    <td className="num mono">{n2(Math.abs(r.running))} <small style={{ color: "var(--muted)" }}>{drcr(r.running)}</small></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td colSpan={2}>Totals</td><td className="num mono">{n2(data.total_debit)}</td><td className="num mono">{n2(data.total_credit)}</td><td /></tr>
                <tr><td colSpan={4}>Closing balance</td><td className="num mono">{n2(Math.abs(data.closing_balance))} {drcr(data.closing_balance)}</td></tr>
              </tfoot>
            </table>
          </div>
        </section>
      ))}
    </main>
  );
}
